import React, { useEffect, useState } from 'react';
import { bulkSetCrmVariable, getCrmVariables } from '../config/api';

const CrmBulkSetVariableDialog = ({ leadIds, onClose, onSaved }) => {
  const [variables, setVariables] = useState([]);
  const [variableId, setVariableId] = useState('');
  const [value, setValue] = useState('');
  const [loadingVariables, setLoadingVariables] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selectedVariable = variables.find(
    (variable) => String(variable.id) === String(variableId)
  );

  useEffect(() => {
    const loadVariables = async () => {
      try {
        const response = await getCrmVariables();
        const result = response?.data || response;
        setVariables(result?.variables || []);
      } catch (err) {
        setError(err.message || 'Could not load CRM variables.');
      } finally {
        setLoadingVariables(false);
      }
    };
    loadVariables();
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (!selectedVariable) {
      setError('Select a variable.');
      return;
    }
    if (value === '') {
      setError(selectedVariable.type === 'list' ? 'Select a value.' : 'Enter a value.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await bulkSetCrmVariable(
        leadIds,
        selectedVariable.id,
        value
      );
      if (onSaved) await onSaved(response?.data || response);
      onClose();
    } catch (err) {
      setError(err.message || 'Could not set the variable.');
    } finally {
      setSaving(false);
    }
  };

  const renderValueField = () => {
    if (!selectedVariable) return null;
    if (selectedVariable.type === 'list') {
      return (
        <select
          id="crm-bulk-variable-value"
          className="form-control"
          value={value}
          onChange={(event) => {
            setValue(event.target.value);
            setError('');
          }}
          disabled={saving}
        >
          <option value="">Select a value</option>
          {(selectedVariable.allowedValues || []).map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      );
    }

    return (
      <input
        id="crm-bulk-variable-value"
        className="form-control"
        type={selectedVariable.type === 'date' ? 'date' : selectedVariable.type === 'number' ? 'number' : 'text'}
        step={selectedVariable.type === 'number' ? 'any' : undefined}
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
          setError('');
        }}
        disabled={saving}
        placeholder={selectedVariable.type === 'text' ? `Enter ${selectedVariable.label}` : undefined}
      />
    );
  };

  return (
    <div className="modal-overlay" onClick={saving ? undefined : onClose}>
      <div className="modal-content" onClick={(event) => event.stopPropagation()} style={{ maxWidth: '540px' }}>
        <form onSubmit={submit}>
          <div className="modal-header">
            <h3>Set Variable</h3>
            <button type="button" className="modal-close" onClick={onClose} disabled={saving}>×</button>
          </div>
          <div className="modal-body">
            <p className="crm-muted">
              Set one variable on {leadIds.length} selected Lead{leadIds.length === 1 ? '' : 's'}.
            </p>
            <div className="form-group">
              <label className="form-label" htmlFor="crm-bulk-variable">Variable</label>
              <select
                id="crm-bulk-variable"
                className="form-control"
                value={variableId}
                onChange={(event) => {
                  setVariableId(event.target.value);
                  setValue('');
                  setError('');
                }}
                disabled={loadingVariables || saving}
                autoFocus
              >
                <option value="">{loadingVariables ? 'Loading variables…' : 'Select a variable'}</option>
                {variables.map((variable) => (
                  <option key={variable.id} value={variable.id}>
                    {variable.label} ({variable.type})
                  </option>
                ))}
              </select>
              {!loadingVariables && variables.length === 0 && (
                <div className="crm-form-help">No active admin-defined variables are available.</div>
              )}
            </div>
            {selectedVariable && (
              <div className="form-group">
                <label className="form-label" htmlFor="crm-bulk-variable-value">Value</label>
                {renderValueField()}
              </div>
            )}
            {error && <div className="crm-form-error">{error}</div>}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving || !selectedVariable || value === ''}>
              {saving ? 'Saving…' : 'Set Variable'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CrmBulkSetVariableDialog;
